
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  coursesQuery,
  progressScoresQuery,
} from '../api';

import { navigation } from '../data';
import { gameTypes } from './data';
import SubPagesLayout from '../../../UI/layout/subPages';

import './styles.css';
import Table from '../../../UI/table';

export default function InstructorProgress() {
  const navigate = useNavigate();

  // Single dedicated endpoint replaces the old fan-out of courseMembers +
  // games + sessions + client-side aggregation. Courses call is a tiny
  // hop to resolve the course id the aggregated endpoint requires, and
  // hits the shared react-query cache alongside other insights pages.
  const { data: courses } = useQuery(coursesQuery());
  const courseId = courses?.[0]?.id;

  const { data: progressScores } = useQuery(progressScoresQuery({ courseId }));

  const { headers, rows, progressInfo } = useMemo(() => {
    if (!progressScores) return { headers: [], rows: [], progressInfo: null };

    const games = progressScores.games || [];
    const students = progressScores.students || [];

    const tableHeaders = [
      'Student\'s Name',
      'Overall',
      ...games.map(game => `${game.name}<br><span class="thin">${gameTypes[game.game_type]}</span>`),
    ];

    const tableRows = students.map(student => {
      const perGame = student.per_game || {};
      const cells = games.map(game => perGame[String(game.id)]?.status || 'Not Started');

      // Overall = Completed only if every game is Completed; In Progress
      // if any game has been touched; otherwise Not Started. Mirrors the
      // prior client-side computation over sessions.
      const anyStarted = cells.some(status => status !== 'Not Started');
      const allCompleted = cells.length > 0 && cells.every(status => status === 'Completed');
      const overall = allCompleted
        ? 'Completed'
        : anyStarted
          ? 'In Progress'
          : 'Not Started';

      return [
        `${student.first_name}${student.last_name ? ` ${student.last_name}` : ''}`,
        overall,
        ...cells,
      ];
    });

    const startedCount = tableRows.filter(row => row[1] !== 'Not Started').length;
    const completedCount = tableRows.filter(row => row[1] === 'Completed').length;
    const totalStudents = students.length;

    let info = <p>No student has started an assignment.</p>;
    if (totalStudents > 0 && completedCount === totalStudents) {
      info = <p>Every student has finished all the assignments.</p>;
    } else if (startedCount > 0) {
      info = <p>As of today, <b>{startedCount}</b> out of <b>{totalStudents}</b> students in the class have started at least one assignment, with <b>{completedCount}</b> completing all assignments.</p>;
    }

    return { headers: tableHeaders, rows: tableRows, progressInfo: info };
  }, [progressScores]);

  const handlePrevButtonClick = () => {
    // Decision Insights → Overall previously required a games list to
    // pick the right sub-page (Principles vs Double-Entry vs Classifying
    // Normal). The aggregated endpoint gives us the same signal — infer
    // from which game types exist in this course.
    let redirectUrl = '/instructor/insights/decision-insights/overall';
    const gameTypesInCourse = new Set((progressScores?.games || []).map(g => g.game_type));
    if (gameTypesInCourse.has('The Principles')) {
      redirectUrl = '/instructor/insights/decision-insights/principles';
    } else if (gameTypesInCourse.has('Balanced Equation')) {
      redirectUrl = '/instructor/insights/decision-insights/double-entry';
    } else if (gameTypesInCourse.has('Account Classification') || gameTypesInCourse.has('Normal Balance')) {
      redirectUrl = '/instructor/insights/decision-insights/classifying-normal';
    }
    navigate(redirectUrl);
  }

  const handleNextButtonClick = () => {
    navigate('/instructor/insights/progress-scores/scores' );
  }

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      onSecondButtonClick={handleNextButtonClick}
      currentPage={1}
      pagesQuantity={1}
      contentClassName="decision-insights-content-container"
    >
      <h1>Insights</h1>
      <h2>Progress</h2>
      {progressInfo}

      <Table
        headers={headers}
        rows={rows}
        rowsPerPageOptions={[50, 75, 100]}
      />
    </SubPagesLayout>
  );
}
